import { useCallback, useEffect, useState } from "react";
import { Show } from "@clerk/react";
import { Redirect, useSearch } from "wouter";
import {
  readStoredCredential,
  setActiveScreenCredential,
} from "@/player/lib/screen-credential";
import PlayerRuntime from "@/player/player-runtime";
import PlayerLogin from "@/player/pages/player-login";
import Handoff from "@/player/pages/handoff";

/**
 * The `/player` entrypoint. Resolution order:
 *  1. `?activate=<sessionId>` — phone handoff: a signed-in phone activating a
 *     screen elsewhere.
 *  2. A persisted durable credential — resume playback (auto-resume on reboot).
 *  3. Otherwise pick a device: signed-out shows login + QR, signed-in redirects
 *     to the dedicated player-selection page.
 */
export default function PlayerEntry() {
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const activateId = searchParams.get("activate");
  // Set when another window just claimed a device for us (desktop hand-off):
  // the credential write may still be in flight, so wait for it briefly
  // instead of bouncing the operator back into the device picker.
  const handoffDeviceId = searchParams.get("device");
  const storedCredential = readStoredCredential();

  if (storedCredential) {
    setActiveScreenCredential(storedCredential);
  }

  const [hasCredential, setHasCredential] = useState(() =>
    Boolean(storedCredential),
  );
  const [handoffTimedOut, setHandoffTimedOut] = useState(false);
  const awaitingHandoff = Boolean(
    handoffDeviceId && !hasCredential && !handoffTimedOut,
  );

  useEffect(() => {
    if (!awaitingHandoff) return;
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const stored = readStoredCredential();
      if (stored) {
        setActiveScreenCredential(stored);
        setHasCredential(true);
        window.clearInterval(interval);
        return;
      }
      if (Date.now() - startedAt > 3000) {
        setHandoffTimedOut(true);
        window.clearInterval(interval);
      }
    }, 150);
    return () => window.clearInterval(interval);
  }, [awaitingHandoff]);

  const onCredential = useCallback(() => setHasCredential(true), []);
  const onExit = useCallback(() => {
    setActiveScreenCredential(null);
    setHasCredential(false);
  }, []);

  useEffect(() => {
    if (hasCredential) {
      const stored = readStoredCredential();
      setActiveScreenCredential(stored);
    } else {
      setActiveScreenCredential(null);
    }
  }, [hasCredential]);

  useEffect(() => {
    return () => {
      setActiveScreenCredential(null);
    };
  }, []);

  // Phone handoff takes priority — this device is activating a different screen,
  // it should not start playing here.
  if (activateId) {
    return <Handoff sessionId={activateId} />;
  }

  if (hasCredential) {
    return <PlayerRuntime onExit={onExit} />;
  }

  // Waiting for the just-claimed credential to land in storage.
  if (awaitingHandoff) {
    return null;
  }

  return (
    <>
      <Show when="signed-out">
        <PlayerLogin onActivated={onCredential} />
      </Show>
      <Show when="signed-in">
        <Redirect
          to={
            handoffDeviceId
              ? `/player-selection?device=${encodeURIComponent(handoffDeviceId)}`
              : "/player-selection"
          }
        />
      </Show>
    </>
  );
}
